import { Backdrop, Box } from "@mui/material";
import AppLoader from '../../components/AppLoader/AppLoader';
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cfpBaseUrl } from "../../network/urls";
import { getDate, getMonthName, getYear, companyName } from "../../services/Functions";
import DashboardCompanyToolbar from "../../components/cfcDashboard/DashboardCompanyToolbar";
import {
  getInitialAdminOfficeCode,
  isDiscoAdminUser,
  resolveCfcDashboardOfficeCode,
} from "../../utils/adminCompany";
import "./CfcDashboard.scss";
const DashboardHeader = lazy(() => import("../../components/cfcDashboard/DashboardHeader"));
const InfoCard = lazy(() => import("../../components/cfcDashboard/InfoCard"));

const CfcDashboard = () => {
  const [viewBackdrop, setViewBackdrop] = useState(false);

  const { token, code, roleId, selectedAdminCompanyCode } = useSelector(
    (state) => state.auth,
  );
  const showCompanyToolbar = isDiscoAdminUser(code, roleId);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState(
    getInitialAdminOfficeCode(code, selectedAdminCompanyCode),
  );
  const [activeCompanyCode, setActiveCompanyCode] = useState(
    getInitialAdminOfficeCode(code, selectedAdminCompanyCode),
  );
  const [dashboardData, setDashboardData] = useState([]);
  const [dynamicServices, setDynamicServices] = useState([]);

  // Color palette for dynamic service cards
  const cardColors = ["#FFE2E5", "#EBDBFE", "#FEF5C0", "#FBE2CC", "#DCFCE7", "#CCE5FF", "#F8D7DA", "#E2E3E5"];

  // Service mapping for grouping related services
  const serviceMapping = {
    'Change of Name': 'Change of Attributes',
    'Change of Load': 'Change of Attributes',
    'Change of Tariff': 'Change of Attributes',
    'Change Of Attributes': 'Change of Attributes',
  };

  // Helper to split service name into title1 and title2
  const splitServiceName = (serviceName) => {
    const words = serviceName.split(' ');
    if (words.length === 1) {
      return { title1: serviceName, title2: '' };
    } else if (words.length === 2) {
      return { title1: words[0], title2: words[1] };
    } else {
      const midPoint = Math.ceil(words.length / 2);
      return {
        title1: words.slice(0, midPoint).join(' '),
        title2: words.slice(midPoint).join(' ')
      };
    }
  };

  const [serviceCards, setServiceCards] = useState([])

  const getActiveCompanyLabel = () => {
    const activeCode = showCompanyToolbar ? activeCompanyCode : String(code).slice(0, 2);
    const fullName = companyName(String(activeCode));
    return fullName !== "DISTRIBUTION COMPANY NAME" ? fullName : "";
  };

  const loadDashboardData = useCallback(
    async (companyCode) => {
      if (!token) return;
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);

      const formdata = new FormData();
      const apiOfficeCode = resolveCfcDashboardOfficeCode(
        companyCode,
        code,
        roleId,
        companyCode,
      );
      formdata.append("officeCode", apiOfficeCode);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "comman/getCFCDashboardStats",
          requestOptions,
        );
        const result = await response.json();
        if (result.status === "200") {
          setDashboardData(result.data);

          if (result.data) {
            const yearlyData = result.data.filter(
              (item) => item.Duration === "Progressive",
            );
            const currentMonthData = result.data.filter(
              (item) => item.Duration === "CurrentMonth",
            );

            const serviceDataMap = {};
            const uniqueServicesSet = new Set();

            result.data.forEach((item) => {
              const mappedService = serviceMapping[item.Service] || item.Service;
              uniqueServicesSet.add(mappedService);
            });

            yearlyData.forEach((data) => {
              const mappedService = serviceMapping[data.Service] || data.Service;
              if (!serviceDataMap[mappedService]) {
                serviceDataMap[mappedService] = { yearly: 0, monthly: 0 };
              }
              serviceDataMap[mappedService].yearly += data.Total;
            });

            currentMonthData.forEach((data) => {
              const mappedService = serviceMapping[data.Service] || data.Service;
              if (!serviceDataMap[mappedService]) {
                serviceDataMap[mappedService] = { yearly: 0, monthly: 0 };
              }
              serviceDataMap[mappedService].monthly += data.Total;
            });

            const uniqueServicesList = Array.from(uniqueServicesSet);
            setDynamicServices(uniqueServicesList);

            const dynamicServiceCards = Object.keys(serviceDataMap).map(
              (serviceName, index) => {
                const { title1, title2 } = splitServiceName(serviceName);
                return {
                  service: serviceName,
                  title1,
                  title2,
                  yearlyCounter: serviceDataMap[serviceName].yearly,
                  month: new Date().toLocaleString("default", { month: "short" }),
                  monthlyCounter: serviceDataMap[serviceName].monthly,
                  color: cardColors[index % cardColors.length],
                };
              },
            );

            setServiceCards(dynamicServiceCards);
          }
        }
      } catch (loadError) {
        setDashboardData([]);
        setDynamicServices([]);
        setServiceCards([]);
      } finally {
        setViewBackdrop(false);
      }
    },
    [token, code, roleId],
  );

  useEffect(() => {
    if (showCompanyToolbar && selectedAdminCompanyCode) {
      const company = String(selectedAdminCompanyCode);
      setSelectedCompanyCode(company);
      setActiveCompanyCode(company);
    }
  }, [showCompanyToolbar, selectedAdminCompanyCode]);

  useEffect(() => {
    if (showCompanyToolbar) {
      if (activeCompanyCode) {
        loadDashboardData(activeCompanyCode);
      }
      return;
    }
    loadDashboardData(code);
  }, [showCompanyToolbar, activeCompanyCode, code, loadDashboardData]);

  const handleShowCompany = () => {
    if (selectedCompanyCode) {
      setActiveCompanyCode(selectedCompanyCode);
    }
  };

  return (
    <>
      <Box className="form-box">
        <div className="cfc-dashboard">
          {showCompanyToolbar ? (
            <DashboardCompanyToolbar
              selectedCompanyCode={selectedCompanyCode}
              onCompanyChange={setSelectedCompanyCode}
              onShow={handleShowCompany}
              loading={viewBackdrop}
              showDisabled={selectedCompanyCode === activeCompanyCode}
              activeCompanyLabel={getActiveCompanyLabel()}
              activeCompanyCode={activeCompanyCode}
            />
          ) : null}
          <Suspense fallback={<div>Loading header...</div>}>
            <DashboardHeader
              title="Customer Services"
              serviceCards={serviceCards}
            />
          </Suspense>
          <div className="info-cards">
            <Suspense fallback={<div>Loading card...</div>}>
              <InfoCard title="Services" type="services" rows={dynamicServices.map(service => ({ service }))} />
              <InfoCard
                title="Progressive"
                subTitle={getYear()}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "Progressive", dynamicServices, serviceMapping)}
                type="progressive"
              />
              <InfoCard
                title="Monthly"
                subTitle={getMonthName(0)}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "CurrentMonth", dynamicServices, serviceMapping)}
                type="progressive"
              />
              <InfoCard
                title="Daily"
                subTitle={getDate()}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "Daily", dynamicServices, serviceMapping)}
                type="progressive"
              />
            </Suspense>
          </div>
        </div>
      </Box>

      {/* Backdrop */}
      < Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <AppLoader />
      </Backdrop >

    </>
  );
};

// Dynamic helper function to align data with services list
// Supports service mapping (e.g., Change of Name/Load/Tariff -> Change of Attributes)
export const getAlignedDataByDurationDynamic = (dashboardData, duration, servicesList, serviceMapping = {}) => {
  const filteredData = dashboardData.filter(item => item.Duration === duration);

  // Create a map to aggregate data for mapped services
  const serviceDataMap = {};
  servicesList.forEach(service => {
    serviceDataMap[service] = { pending: 0, attended: 0, total: 0 };
  });

  filteredData.forEach(item => {
    const mappedService = serviceMapping[item.Service] || item.Service;
    if (serviceDataMap[mappedService]) {
      serviceDataMap[mappedService].pending += item.Pending || 0;
      serviceDataMap[mappedService].attended += item.Attended || 0;
      serviceDataMap[mappedService].total += item.Total || 0;
    }
  });

  return servicesList.map(serviceName => ({
    service: serviceName,
    pending: serviceDataMap[serviceName].pending || null,
    attended: serviceDataMap[serviceName].attended || null,
    total: serviceDataMap[serviceName].total || null
  }));
};

export default CfcDashboard;
